import { FAIcon } from '@/components'
import { TextEllipsisLoader } from '@/components/loader'
import { Progress } from '@/components/ui'
import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useEffect } from 'react'
// eslint-disable-next-line no-restricted-imports
import { toast } from 'sonner'
import { SALARY_LOCK_RUN } from '../queries'

interface SalaryLockRunToastProps {
  runId: string
}

/**
 * Progress for a company-wide "lock all". The run is polled rather than pushed,
 * so the toast keeps reporting even if the user leaves the salary page — locking
 * a whole company's month takes minutes.
 */
function SalaryLockRunToast({ runId }: SalaryLockRunToastProps) {
  const translations = useTranslate({
    queued: 'salary.lock-all-toast-queued',
    inProgress: 'salary.lock-all-toast-in-progress',
    completed: 'salary.lock-all-toast-completed',
    failed: 'salary.lock-all-toast-failed',
    partial: 'salary.lock-all-toast-partial',
  })

  const { data, stopPolling } = useQuery(SALARY_LOCK_RUN, {
    variables: { id: runId },
    pollInterval: 2000,
    fetchPolicy: 'network-only',
  })

  const run = data?.salaryLockRun
  const done = run?.status === 'COMPLETED' || run?.status === 'FAILED'
  const errors = run?.errors ?? []

  useEffect(() => {
    if (done) stopPolling()
  }, [done, stopPolling])

  function pendingLabel() {
    if (!run) return translations.inProgress
    // The run only gets its user count once a worker takes it off the queue.
    if (run.status === 'PENDING') return translations.queued
    return `${translations.inProgress} (${run.processedUsers}/${run.totalUsers})`
  }

  if (!run || !done)
    return (
      <div className="flex w-full flex-col gap-2">
        <TextEllipsisLoader>{pendingLabel()}</TextEllipsisLoader>
        <Progress className="h-2" value={run?.progress ?? 0} />
      </div>
    )

  if (run.status === 'FAILED')
    return (
      <div className="flex flex-col gap-1">
        <span className="flex items-center gap-2">
          <FAIcon
            className="text-red-600"
            icon="exclamation-circle"
            type="solid"
            fixedWidth
          />
          {translations.failed}
        </span>
        {errors.map(({ error }, index) => (
          <span key={index} className="text-xs text-stone-600">
            {error}
          </span>
        ))}
      </div>
    )

  return (
    <div className="flex flex-col gap-1">
      <span className="flex items-center gap-2">
        <FAIcon
          className={errors.length ? 'text-amber-600' : 'text-green-600'}
          icon={errors.length ? 'triangle-exclamation' : 'check-circle'}
          type="solid"
          fixedWidth
        />
        {errors.length
          ? `${translations.partial} (${errors.length})`
          : translations.completed}
      </span>
      {errors.map(({ user, error }, index) => (
        <span key={`${user}-${index}`} className="text-xs text-stone-600">
          {user}: {error}
        </span>
      ))}
    </div>
  )
}

export function salaryLockRunToast(runId: string) {
  const id = `salary-lock-run-${runId}`

  return toast(<SalaryLockRunToast runId={runId} />, {
    id,
    classNames: { content: 'w-full' },
    duration: Infinity,
    closeButton: true,
  })
}
